/* tiptap-utils.ts and advanced-tiptap-utils.ts is overwritten by CLI so our custom utils should be here */

import { getMaImageUrl } from '@/components/image-library/image-library-utils';
import type { ZyDocumentAttributes } from '@/components/tiptap-editor/tiptap-extension/zydocument-extension';
import { zyVersionFilterPluginKey } from '@/components/tiptap-editor/tiptap-extension/zyversion-extension/zyversion-filter-plugin';
import {
    EMPTY_ZY_VERSION_REGISTRY,
    toPublicRegistryState,
    zyVersionRegistryPluginKey,
    type ZyVersionRegistryState,
} from '@/components/tiptap-editor/tiptap-extension/zyversion-extension/zyversion-registry-plugin';
import { ACCEPTED_IMAGE_TYPES, MAX_FILE_SIZE } from '@/lib/utils';
import authService from '@/services/zybook-auth-service';
import { Fragment, Node, type NodeSpec, type ResolvedPos } from '@tiptap/pm/model';
import { Transaction } from '@tiptap/pm/state';
import type { Content, Editor, JSONContent } from '@tiptap/react';
import { findParentNodeClosestToPos } from '@tiptap/react';
import { toast } from 'sonner';
import type { SelectedZyVersionFilter } from './zy-tiptap-utils-core';
import { uploadImageToS3, type MaImage, type MaImageDocument, type UploadedImageResponse } from './zybooks-api';

export { stripListItemParagraph, stripOuterParagraphs } from './tiptap-dom-html-utils';
export {
    getDescendantOfType,
    isZyVersionNodeVisible,
    normalizeSelectedZyVersionFilter,
    renderJsonTo,
    type SelectedZyVersionFilter,
} from './zy-tiptap-utils-core';

export function getQuestionSetQuestionNumber(getPos: () => number | undefined, editor: Editor, questionType: string): number {
    const pos = getPos();
    if (typeof pos !== 'number') {
        return 1;
    }

    // Resolve position of the node
    const resolvedPos = editor.state.doc.resolve(pos + 1);

    // Find the question depth
    let questionDepth = -1;

    // Check if current node is of questionType
    if (resolvedPos.node().type.name === questionType) {
        questionDepth = resolvedPos.depth;
    } else {
        // Search upwards for the questionType node
        for (let depth = resolvedPos.depth; depth >= 0; depth--) {
            if (resolvedPos.node(depth).type.name === questionType) {
                questionDepth = depth;
                break;
            }
        }
    }

    if (questionDepth === -1) {
        return 1;
    }

    // Find questionSet depth
    let questionSetDepth = -1;
    for (let depth = questionDepth - 1; depth >= 0; depth--) {
        if (resolvedPos.node(depth).type.name === 'questionSet') {
            questionSetDepth = depth;
            break;
        }
    }

    if (questionSetDepth === -1) {
        return 1;
    }

    // Get the index of the question node within its parent
    const questionIndex = resolvedPos.index(questionDepth - 1);

    // Count only question nodes up to this index
    const questionSetNode = resolvedPos.node(questionSetDepth);
    let count = 0;
    for (let i = 0; i < questionIndex; i++) {
        if (questionSetNode.child(i).type.name === questionType) {
            count++;
        }
    }

    return count + 1;
}

export function getBasicContentSchema(options?: {
    requireAtLeastOneNode?: boolean;
    additionalContentTypes?: string[];
    restrictedContentTypes?: string[];
}): NodeSpec['content'] {
    const { requireAtLeastOneNode = true, additionalContentTypes = [], restrictedContentTypes = [] } = options || {};

    const contentTypes = [
        // Standard content types that are always available
        'paragraph',
        'blockquote',
        'bulletList',
        'codeBlock',
        'heading',
        'horizontalRule',
        'orderedList',
        'taskList',
        'blockMath',
        'details',
        'detailsContent',
        'detailsSummary',
        'table',
        'image',
        'imageUpload',
        'zyCode',
        'zyConsole',

        // Available without seeAllMAContent (lists / ZyVersion allow-list)
        'zyVersionBlock',
        // Remaining custom content types that require the `canSeeAllMAContent` flag
        ...(authService.canSeeAllMAContent() ? ['iframe', ...restrictedContentTypes] : []),

        // Additional content types, passed in as options
        ...additionalContentTypes,
    ].join(' | ');

    return `(${contentTypes})${requireAtLeastOneNode ? '+' : '*'}`;
}

/**
 * Finds the nearest ancestor node of the given type by walking up from a resolved position.
 * @param $pos - The resolved position (e.g. from doc.resolve(pos))
 * @param parentNodeType - The type name of the ancestor node to search for
 * @returns The ancestor node if found, otherwise null
 */
export function getAncestorNodeOfType($pos: ResolvedPos, parentNodeType: string): Node | null {
    let depth = $pos.depth;
    while (depth > 0) {
        const node = $pos.node(depth);
        if (node.type.name === parentNodeType) {
            return node;
        }
        depth--;
    }
    return null;
}

/**
 * Gets the parent node of a given type at a given position
 * @param editor - The Tiptap editor instance
 * @param parentNodeType - The type name of the parent node to search for
 * @param nodePos - The position of the node to check
 * @returns The parent node if found, otherwise null
 */
export function getParentNodeOfType(editor: Editor | null, parentNodeType: string, nodePos?: number): Node | null {
    if (!editor) return null;
    if (nodePos === undefined) {
        nodePos = editor.state.selection.$from.pos;
    }

    const docSize = editor.state.doc.content.size;
    if (nodePos < 0 || nodePos > docSize) {
        console.warn(`Position ${nodePos} is out of bounds (doc size: ${docSize})`);
        return null;
    }

    try {
        const $pos = editor.state.doc.resolve(nodePos);
        return getAncestorNodeOfType($pos, parentNodeType);
    } catch (error) {
        console.error('Error resolving position:', error);
        return null;
    }
}

/**
 * Checks if the current selection is within a descendant of a node with the specified type
 * @param editor - The Tiptap editor instance
 * @param parentNodeType - The type name of the parent node to search for
 * @returns true if both the start and end of the selection are descendants of the specified parent type, false otherwise
 */
export function isSelectionDescendantOf(editor: Editor | null, parentNodeType: string): boolean {
    if (!editor) return false;
    const { selection } = editor.state;
    const { $from, $to } = selection;
    const parentNodeFrom = getParentNodeOfType(editor, parentNodeType, $from.pos);
    const parentNodeTo = getParentNodeOfType(editor, parentNodeType, $to.pos);
    return parentNodeFrom !== null && parentNodeTo !== null;
}

/**
 * Document position immediately before the zyTerm ancestor that contains this resolved position, or null.
 */
export function getZyTermAncestorStartPos($pos: ResolvedPos): number | null {
    let depth = $pos.depth;
    while (depth > 0) {
        if ($pos.node(depth).type.name === 'zyTerm') {
            return $pos.before(depth);
        }
        depth--;
    }
    return null;
}

/**
 * True when the caret is inside a zyTerm, or when a non-empty selection lies entirely within a single zyTerm
 * (both endpoints share the same zyTerm ancestor). Mixed selections (e.g. paragraph text plus part of a term) return false.
 */
export function isSelectionOnlyInsideZyTerm(editor: Editor | null): boolean {
    if (!editor) return false;
    const { empty, $from, $to } = editor.state.selection;

    const startFrom = getZyTermAncestorStartPos($from);
    if (empty) {
        return startFrom !== null;
    }

    const startTo = getZyTermAncestorStartPos($to);
    if (startFrom === null || startTo === null) {
        return false;
    }

    return startFrom === startTo;
}

/**
 * Sets an attribute on a node in the editor
 * @param tr - The transaction to modify
 * @param dispatch - The dispatch function to apply the transaction
 * @param pos - The position of the node to set the attribute on
 * @param attributeName - The name of the attribute to set
 * @param value - The value of the attribute to set
 */
export function setNodeAttribute(tr: Transaction, dispatch: (tr: Transaction) => void, pos: number, attributeName: string, value: any) {
    tr.setNodeAttribute(pos, attributeName, value);
    dispatch(tr);
}

/**
 * Checks if the current selection is at the top level of the document.
 * In ProseMirror/TipTap's document structure, depth 1 represents the first level
 * of nodes directly under the document root (depth 0).
 *
 * @param editor - The TipTap editor instance to check the selection from
 * @returns `true` if the selection is at depth 1 (top level node), `false` otherwise
 */
export function isSelectionTopNode(editor: Editor) {
    return editor.state.selection.$from.depth === 1 || editor.state.selection.$from.depth === 0;
}

/**
 * Handles image upload with progress tracking and abort capability
 * @param file The file to upload
 * @param documentId Optional MA document to link the image to
 * @returns Promise resolving to the uploaded image and optional document link
 */
export const handleImageUpload = async (
    file: File,
    documentId?: number,
    onProgress?: (event: { progress: number }) => void,
    signal?: AbortSignal,
): Promise<UploadedImageResponse> => {
    if (!file) {
        throw new Error('No file provided');
    }

    try {
        return await uploadImageToS3(file, documentId, onProgress, signal);
    } catch (error) {
        console.error('Error uploading image:', error);
        throw new Error('Image upload failed', { cause: error });
    }
};

const insertImageAt = (editor: Editor, maImage: MaImage, maImageDocument: MaImageDocument | null, insertAt?: number): boolean => {
    const attrs = {
        src: getMaImageUrl(maImage),
        alt: maImage.alt_text ?? '',
        maImageId: maImage.ma_image_id,
        maImageNodeId: maImageDocument?.ma_image_node_uuid ?? null,
    };
    if (insertAt != null) {
        return editor
            .chain()
            .focus()
            .insertContentAt(insertAt, {
                type: 'image',
                attrs: attrs,
            })
            .run();
    }

    return editor
        .chain()
        .focus()
        .insertContent({
            type: 'image',
            attrs: attrs,
        })
        .run();
};

export const uploadAndInsertImageFromFile = (editor: Editor | null | undefined, file: File, insertAt?: number): boolean => {
    if (!editor?.isEditable) {
        return true;
    }

    if (file.size > MAX_FILE_SIZE) {
        toast.error('Upload failed', {
            description: `File size exceeds maximum allowed (${MAX_FILE_SIZE / 1024} KB)`,
        });
        return true;
    }

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast.error('Upload failed', {
            description: `File type not accepted (${file.type})`,
        });
        return true;
    }

    const canInsert =
        insertAt != null ? editor.can().insertContentAt(insertAt, { type: 'image' }) : editor.can().insertContent({ type: 'image' });

    if (!canInsert) {
        return true;
    }

    // const { uploadImagesToMATESTBucket } = editor.state.doc.attrs;
    const { documentId } = editor.state.doc.attrs;

    // if (uploadImagesToMATESTBucket) {
    //     subjectName = 'MATEST';
    //     releaseId = 'MATEST';
    // }

    void handleImageUpload(file, documentId)
        .then(({ ma_image, ma_image_document }) => {
            insertImageAt(editor, ma_image, ma_image_document, insertAt);
        })
        .catch((error: Error) => {
            console.error('Upload failed:', error);
            toast.error('Upload failed', {
                description: (error as Error).message,
            });
        });

    return true;
};

/**
 * Dispatches a transaction with custom metadata without modifying the document.
 * Useful for triggering plugin behavior or signaling state changes to other parts of the editor.
 *
 * @param editor - The TipTap editor instance
 * @param metaKey - An object containing key-value pairs to set as transaction metadata
 */
export const dispatchMetaTransaction = (editor: Editor, metaKey: Record<string, any>) => {
    const tr = editor.state.tr;
    for (const key in metaKey) {
        tr.setMeta(key, metaKey[key]);
    }
    editor.view.dispatch(tr);
};

/**
 * Sets `renderAsHTML` to true on every image node in the document.
 * Dispatches a single transaction that updates all image node attributes.
 */
export function setAllImagesRenderAsHTML(editor: Editor): void {
    const positions: number[] = [];
    editor.state.doc.descendants((node, pos) => {
        if (node.type.name === 'image') {
            positions.push(pos);
        }
    });
    if (positions.length === 0) return;
    const tr = editor.state.tr;
    for (const pos of positions) {
        tr.setNodeAttribute(pos, 'renderAsHTML', true);
    }
    editor.view.dispatch(tr);
}

/**
 * Finds the first node in the editor's document with the given type name.
 *
 * @param editor - The TipTap editor instance
 * @param typeName - The name of the node type to find
 * @returns An object with the found node and its position, or null if not found
 */
export function findNodeByType(editor: Editor, typeName: string): { node: Node; pos: number } | null {
    let result: { node: Node; pos: number } | null = null;
    editor.state.doc.descendants((node, pos) => {
        if (!result && node.type.name === typeName) {
            result = { node, pos };
            return false;
        }
    });
    return result;
}

/**
 * Finds all nodes in the editor's document with the given type name.
 *
 * @param editor - The TipTap editor instance
 * @param typeName - The name of the node type to find
 * @returns An array of objects containing the found nodes and their positions
 */
export function findAllNodesByType(editor: Editor, typeName: string) {
    const results: { node: Node; pos: number }[] = [];
    editor.state.doc.descendants((node, pos) => {
        if (node.type.name === typeName) {
            results.push({ node, pos });
        }
    });
    return results;
}

export const LIST_NODE_TYPES = ['bulletList', 'orderedList', 'taskList'] as const;
export const CONVERTIBLE_BLOCK_TYPES = ['paragraph', 'heading', 'blockquote', 'codeBlock'] as const;

/**
 * Finds the closest ancestor node to target for a list toggle operation.
 *
 * Prefers the closest list ancestor (bulletList/orderedList/taskList) so that
 * a cursor inside a list item inside a container (e.g. blockquote) targets the
 * list itself rather than the container.  Falls back to the closest ancestor
 * whose type is one of the convertible block types.
 */
export function findClosestListOrConvertibleBlock($pos: ResolvedPos): { pos: number; node: Node } | null {
    const listParent = findParentNodeClosestToPos($pos, node => (LIST_NODE_TYPES as readonly string[]).includes(node.type.name));
    if (listParent) return { pos: listParent.pos, node: listParent.node };

    const blockParent = findParentNodeClosestToPos($pos, node =>
        ([...LIST_NODE_TYPES, ...CONVERTIBLE_BLOCK_TYPES] as string[]).includes(node.type.name),
    );
    if (blockParent) return { pos: blockParent.pos, node: blockParent.node };

    return null;
}

/**
 * Returns the zyVersion registry state for a live editor instance.
 */
export function getZyVersionRegistry(editor: Editor): ZyVersionRegistryState {
    const internal = zyVersionRegistryPluginKey.getState(editor.state);
    return internal ? toPublicRegistryState(internal) : EMPTY_ZY_VERSION_REGISTRY;
}

/**
 * Returns sorted unique versions from the editor's zyVersion registry.
 */
export function getZyVersions(editor: Editor): string[] {
    return getZyVersionRegistry(editor).versions;
}

/**
 * Returns the active zyVersion filter for an editor from plugin state or extension storage.
 */
export function getEditorVersionFilter(editor: Editor | null): SelectedZyVersionFilter {
    if (!editor) {
        return null;
    }

    const pluginState = zyVersionFilterPluginKey.getState(editor.state);
    if (pluginState) {
        return pluginState.versionFilter;
    }

    return editor.storage.zyVersion?.versionFilter ?? null;
}

/**
 * Returns valid zyVersion tokens for this document (from Subjects), set via ZyVersion extension options.
 */
export function getEditorAllowedZyVersions(editor: Editor): string[] {
    return editor.storage.zyVersion?.allowedVersions ?? [];
}

/**
 * Returns true when the editor document contains any zyVersionBlock or zyVersionInline node.
 */
export function hasZyVersionNodes(editor: Editor): boolean {
    return getZyVersionRegistry(editor).hasVersionNodes;
}

/**
 * Returns true if the current editor selection is inside (or overlaps) a
 * `zyVersionBlock` or `zyVersionInline` node.
 *
 * @param editor - The active TipTap editor instance.
 * @returns True if the selection is inside a zyVersion node, false otherwise.
 */
export function isInsideZyVersion(editor: Editor): boolean {
    const { from, to, empty } = editor.state.selection;
    const doc = editor.state.doc;
    const inlineType = editor.schema.nodes.zyVersionInline;
    const blockType = editor.schema.nodes.zyVersionBlock;

    if (empty) {
        const $pos = doc.resolve(from);
        for (let d = $pos.depth; d > 0; d--) {
            const node = $pos.node(d);
            if (node.type === inlineType || node.type === blockType) return true;
        }
        return false;
    }

    let found = false;
    doc.nodesBetween(from, to, node => {
        if (found) return false;
        if (node.type === inlineType || node.type === blockType) {
            found = true;
            return false;
        }
    });
    return found;
}

/**
 * Applies multiple {@link ZyDocumentAttributes} on the document node in one pass by calling
 * `setDocAttribute` for each provided key.
 *
 * @param editor - TipTap editor whose root document attributes should be updated.
 * @param attributes - Subset of zy document attributes to set; omitted keys are left unchanged.
 */
export function setDocAttributes(editor: Editor, attributes: Partial<ZyDocumentAttributes>) {
    if (!editor) {
        console.error('Editor is not initialized yet.');
        return;
    }

    const entries = Object.entries(attributes);
    if (entries.length === 0) {
        console.warn('No attributes to set');
        return;
    }

    queueMicrotask(() => {
        editor.commands.setDocAttributes(attributes);
    });
}

/**
 * Returns a structural copy of a ProseMirror node with `id` set to `null` on the root and
 * on every descendant whose attribute map includes an own property `id`.
 *
 * @param node - The node to copy.
 */
export function copyNode(node: Node): Node {
    if (node.isText) {
        return node;
    }

    const attrs = node.attrs && Object.prototype.hasOwnProperty.call(node.attrs, 'id') ? { ...node.attrs, id: null } : node.attrs;
    if (node.isLeaf && node.content.size === 0) {
        return node.type.create(attrs, node.content, node.marks);
    }

    const mapped: Node[] = [];
    node.content.forEach(child => {
        mapped.push(copyNode(child));
    });
    return node.type.create(attrs, Fragment.from(mapped), node.marks);
}

/**
 * Walks the document between absolute positions `from` and `to` using
 * {@link Node.nodesBetween} and returns the first **block** whose type name
 * equals `nodeName`. Stops at the first match.
 *
 * @param doc - Document to search.
 * @param from - Start bound (absolute position in the document).
 * @param to - End bound (absolute position in the document).
 * @param nodeName - ProseMirror node type name to match (e.g. `'paragraph'`).
 * @returns The matching node and the document position **before** that node, or `null`.
 */
export function findFirstMatchingBlockInRange(doc: Node, from: number, to: number, nodeName: string): { node: Node; pos: number } | null {
    let result: { node: Node; pos: number } | null = null;
    doc.nodesBetween(from, to, (node, pos) => {
        if (node.type.name === nodeName && node.type.isBlock) {
            result = { node, pos };
            return false;
        }
    });
    return result;
}

export function isProseMirrorJsonContent(content: Content | Fragment | Node): content is JSONContent {
    if (typeof content !== 'object' || content === null) {
        return false;
    }
    if (content instanceof Fragment) {
        return false;
    }
    if (content instanceof Node) {
        return false;
    }
    const json = content as JSONContent;
    return typeof json.type === 'string' && json.type === 'doc';
}

/**
 * Returns a copy of the document with every top-level `zyVersionBlock` replaced by its
 * direct children. Nested version blocks and inline version nodes are left unchanged.
 * Does not mutate the input.
 */
export function unwrapTopLevelZyVersionNodes(doc: JSONContent): JSONContent {
    const content = doc.content ?? [];
    const unwrapped: JSONContent[] = [];

    for (const node of content) {
        if (node.type === 'zyVersionBlock') {
            if (node.content) {
                unwrapped.push(...node.content);
            }
            continue;
        }
        unwrapped.push(node);
    }

    return {
        ...doc,
        content: unwrapped,
    };
}
